'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import QRPreviewPanel from './QRPreviewPanel';
import QRTypeSelector from './QRTypeSelector';
import QRContentForm from './QRContentForm';
import QRCustomizationPanel from './QRCustomizationPanel';
import QRActionButtons from './QRActionButtons';

export type QRType = 'url' | 'text' | 'whatsapp' | 'phone' | 'email' | 'maps' | 'wifi' | 'upi';

export interface QROptions {
  fgColor: string;
  bgColor: string;
  transparentBg: boolean;
  style: 'rounded' | 'square' | 'dots';
  logoUrl: string | null;
  logoSize: number;
  errorLevel: 'L' | 'M' | 'Q' | 'H';
  size: 256 | 512 | 1024 | 2048;
}

export interface QRContentValues {
  url: string;
  text: string;
  whatsappPhone: string;
  whatsappMessage: string;
  phone: string;
  emailTo: string;
  emailSubject: string;
  emailBody: string;
  mapsQuery: string;
  wifiSsid: string;
  wifiPassword: string;
  wifiEncryption: 'WPA' | 'WEP' | 'nopass';
  upiId: string;
  upiName: string;
  upiAmount: string;
  upiNote: string;
}

const defaultContent: QRContentValues = {
  url: '',
  text: '',
  whatsappPhone: '',
  whatsappMessage: '',
  phone: '',
  emailTo: '',
  emailSubject: '',
  emailBody: '',
  mapsQuery: '',
  wifiSsid: '',
  wifiPassword: '',
  wifiEncryption: 'WPA',
  upiId: '',
  upiName: '',
  upiAmount: '',
  upiNote: '',
};

const defaultOptions: QROptions = {
  fgColor: '#0F172A',
  bgColor: '#FFFFFF',
  transparentBg: false,
  style: 'rounded',
  logoUrl: null,
  logoSize: 20,
  errorLevel: 'M',
  size: 512,
};

function buildQRContent(type: QRType, values: QRContentValues): string {
  switch (type) {
    case 'url': {
      let u = values.url.trim();
      if (u && !u.startsWith('http://') && !u.startsWith('https://')) u = 'https://' + u;
      return u;
    }
    case 'text':
      return values.text;
    case 'whatsapp': {
      const num = values.whatsappPhone.replace(/\D/g, '');
      const msg = encodeURIComponent(values.whatsappMessage);
      return `https://wa.me/${num}?text=${msg}`;
    }
    case 'phone':
      return `tel:${values.phone}`;
    case 'email':
      return `mailto:${values.emailTo}?subject=${encodeURIComponent(values.emailSubject)}&body=${encodeURIComponent(values.emailBody)}`;
    case 'maps':
      return `https://maps.google.com/?q=${encodeURIComponent(values.mapsQuery)}`;
    case 'wifi':
      return `WIFI:T:${values.wifiEncryption};S:${values.wifiSsid};P:${values.wifiPassword};;`;
    case 'upi':
      return `upi://pay?pa=${values.upiId}&pn=${encodeURIComponent(values.upiName)}&am=${values.upiAmount}&tn=${encodeURIComponent(values.upiNote)}`;
    default:
      return '';
  }
}

export default function QRGeneratorClient() {
  const [activeType, setActiveType] = useState<QRType>('url');
  const [content, setContent] = useState<QRContentValues>(defaultContent);
  const [options, setOptions] = useState<QROptions>(defaultOptions);
  const [qrValue, setQrValue] = useState('');
  const [isGenerated, setIsGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const validateContent = useCallback((): boolean => {
    const v = content;
    switch (activeType) {
      case 'url':
        if (!v.url.trim()) { setValidationError('Please enter a website URL.'); return false; }
        break;
      case 'text':
        if (!v.text.trim()) { setValidationError('Please enter some text.'); return false; }
        break;
      case 'whatsapp':
        if (!v.whatsappPhone.trim()) { setValidationError('Please enter a WhatsApp phone number.'); return false; }
        break;
      case 'phone':
        if (!v.phone.trim()) { setValidationError('Please enter a phone number.'); return false; }
        break;
      case 'email':
        if (!v.emailTo.trim()) { setValidationError('Please enter an email address.'); return false; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.emailTo)) { setValidationError('Please enter a valid email address.'); return false; }
        break;
      case 'maps':
        if (!v.mapsQuery.trim()) { setValidationError('Please enter a location or address.'); return false; }
        break;
      case 'wifi':
        if (!v.wifiSsid.trim()) { setValidationError('Please enter the WiFi network name (SSID).'); return false; }
        break;
      case 'upi':
        if (!v.upiId.trim()) { setValidationError('Please enter a UPI ID.'); return false; }
        if (!v.upiName.trim()) { setValidationError('Please enter the payee name.'); return false; }
        break;
    }
    setValidationError('');
    return true;
  }, [activeType, content]);

  const handleGenerate = useCallback(async () => {
    if (!validateContent()) return;
    setIsGenerating(true);
    const val = buildQRContent(activeType, content);
    await new Promise(r => setTimeout(r, 400));
    setQrValue(val);
    setIsGenerated(true);
    setIsGenerating(false);

    const historyKey = 'nova-qr-history';
    const existing = JSON.parse(localStorage.getItem(historyKey) || '[]');
    const newRecord = {
      id: `qr-${Date.now()}`,
      type: activeType,
      content: val,
      rawContent: content,
      options,
      createdAt: new Date().toISOString(),
      downloadCount: 0,
    };
    localStorage.setItem(historyKey, JSON.stringify([newRecord, ...existing].slice(0, 100)));

    toast.success('QR Code generated successfully!', {
      description: 'Your QR code is ready to download.',
    });
  }, [validateContent, activeType, content, options]);

  const handleGenerateNew = useCallback(() => {
    setIsGenerated(false);
    setQrValue('');
    setContent(defaultContent);
    setOptions(defaultOptions);
    setActiveType('url');
    setValidationError('');
  }, []);

  const handleTypeChange = useCallback((type: QRType) => {
    setActiveType(type);
    setValidationError('');
    setIsGenerated(false);
    setQrValue('');
  }, []);

  useEffect(() => {
    if (isGenerated) {
      const val = buildQRContent(activeType, content);
      setQrValue(val);
    }
  }, [options, isGenerated, activeType, content]);

  return (
    <div className="min-h-screen qr-dot-pattern">
      {/* ── Hero Section ── */}
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 pt-7 pb-6 sm:pt-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h1
                className="font-extrabold text-foreground leading-tight"
                style={{
                  fontSize: 'clamp(28px, 8vw, 36px)',
                  letterSpacing: '-0.02em',
                  maxWidth: '100%',
                }}
              >
                Generate QR Codes{' '}
                <span className="text-gradient">Instantly</span>
              </h1>
              <p
                className="text-muted-foreground mt-2"
                style={{ fontSize: 'clamp(14px, 3.5vw, 15px)', lineHeight: 1.6 }}
              >
                Convert any link, text, or data into a beautiful high-quality QR code.
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2 gradient-blue-soft rounded-2xl px-4 py-2 border border-border flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-primary"></div>
              <span className="text-primary font-semibold" style={{ fontSize: '12px' }}>Ready to Generate</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 pb-12">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

            {/* Left: Input + Customization */}
            <div className="xl:col-span-2 space-y-5">
              {/* QR Type Selector */}
              <QRTypeSelector activeType={activeType} onTypeChange={handleTypeChange} />

              {/* Content Form */}
              <QRContentForm
                type={activeType}
                values={content}
                onChange={setContent}
                validationError={validationError}
                onClearError={() => setValidationError('')}
              />

              {/* Mobile: QR Preview shown between form and customization */}
              <div className="xl:hidden">
                <QRPreviewPanel
                  qrValue={qrValue}
                  isGenerated={isGenerated}
                  options={options}
                  activeType={activeType}
                  canvasRef={canvasRef}
                />
              </div>

              {/* Customization Panel */}
              <QRCustomizationPanel options={options} onOptionsChange={setOptions} />

              {/* Generate Button — full width, large touch target */}
              <button
                className="btn-primary w-full flex items-center justify-center gap-3"
                onClick={handleGenerate}
                disabled={isGenerating}
                style={{
                  minHeight: '60px',
                  borderRadius: '20px',
                  fontSize: 'clamp(15px, 4vw, 16px)',
                  fontWeight: 600,
                }}
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="8" stroke="white" strokeWidth="2.5" strokeDasharray="25" strokeDashoffset="10" />
                    </svg>
                    Generating QR Code...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="2" y="2" width="6" height="6" rx="1.4" stroke="white" strokeWidth="1.8" />
                      <rect x="12" y="2" width="6" height="6" rx="1.4" stroke="white" strokeWidth="1.8" />
                      <rect x="2" y="12" width="6" height="6" rx="1.4" stroke="white" strokeWidth="1.8" />
                      <rect x="3.6" y="3.6" width="2.4" height="2.4" rx="0.5" fill="white" />
                      <rect x="13.6" y="3.6" width="2.4" height="2.4" rx="0.5" fill="white" />
                      <rect x="3.6" y="13.6" width="2.4" height="2.4" rx="0.5" fill="white" />
                      <rect x="12" y="12" width="2.4" height="2.4" rx="0.5" fill="white" />
                      <rect x="15.6" y="12" width="2.4" height="2.4" rx="0.5" fill="white" />
                      <rect x="12" y="15.6" width="2.4" height="2.4" rx="0.5" fill="white" />
                      <rect x="15.6" y="15.6" width="2.4" height="2.4" rx="0.5" fill="white" />
                    </svg>
                    Generate QR Code
                  </>
                )}
              </button>

              {/* Mobile: Action buttons below generate */}
              {isGenerated && (
                <div className="xl:hidden animate-slide-up">
                  <QRActionButtons
                    qrValue={qrValue}
                    options={options}
                    canvasRef={canvasRef}
                    onGenerateNew={handleGenerateNew}
                  />
                </div>
              )}
            </div>

            {/* Right: Preview (desktop only) */}
            <div className="hidden xl:block xl:col-span-1">
              <div className="sticky top-6">
                <QRPreviewPanel
                  qrValue={qrValue}
                  isGenerated={isGenerated}
                  options={options}
                  activeType={activeType}
                  canvasRef={canvasRef}
                />
                {isGenerated && (
                  <div className="mt-4 animate-slide-up">
                    <QRActionButtons
                      qrValue={qrValue}
                      options={options}
                      canvasRef={canvasRef}
                      onGenerateNew={handleGenerateNew}
                    />
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}